import { useCallback, useMemo, useRef } from 'react';
import isEqual from 'lodash.isequal';
import logCustomRouteUpdated from '@alltrails/amplitude/events/logCustomRouteUpdated';
import logCustomRouteUpdateFailed from '@alltrails/amplitude/events/logCustomRouteUpdateFailed';
import { useAppDispatch, useAppSelector } from '../../redux';
import {
  reverseActiveSegment,
  doubleBackActiveSegment,
  closeLoopActiveSegment,
  removeActiveSegmentHandle,
  setSelectedHandleIndex,
  selectCustomRouteActiveSegment,
  undo as undoAction,
  redo as redoAction,
  updateRouting
} from '../../redux/customRoute';
import { transformPointsToServerRoutes } from '../../redux/customRoutesApi';
import { isSegmentRoutable } from '../../utils/customRoutes/validation';
import useRerouteSegments from './useRerouteSegments';
import useRouting from './useRouting';

type ActionSource = 'toolbar' | 'contextMenu' | 'keyboard';

const useSegmentActions = () => {
  const dispatch = useAppDispatch();
  const { isSmartRouting, segment, selectedHandleIndex, undoStack, redoStack } = useAppSelector(state => ({
    isSmartRouting: state.customRoute.isSmartRouting,
    segment: selectCustomRouteActiveSegment(state),
    selectedHandleIndex: state.customRoute.selectedHandleIndex,
    undoStack: state.customRoute.undoStack,
    redoStack: state.customRoute.redoStack
  }));

  const routing = useRouting();
  const rerouteSegments = useRerouteSegments();
  const isReversingRef = useRef(false);
  const isDoubleBackingRef = useRef(false);

  const isUndoDisabled = useMemo(() => !undoStack || undoStack.length === 0, [undoStack]);
  const isRedoDisabled = useMemo(() => !redoStack || redoStack.length === 0, [redoStack]);

  const { isReverseDisabled, isDoubleBackDisabled, isCloseLoopDisabled } = useMemo(() => {
    const firstPath = segment?.paths[0];
    const firstPoint = firstPath?.points[0];
    const lastPath = segment?.paths[segment.paths.length - 1];
    const lastPoint = lastPath?.points[lastPath.points.length - 1];

    const isRoutable = segment && isSegmentRoutable(segment);
    const hasDifferentStartEnd = isRoutable && !isEqual(firstPoint, lastPoint);

    return {
      isReverseDisabled: !isRoutable,
      isDoubleBackDisabled: !hasDifferentStartEnd,
      isCloseLoopDisabled: !hasDifferentStartEnd
    };
  }, [segment]);

  const undo = useCallback(() => {
    if (isUndoDisabled) {
      return;
    }

    dispatch(undoAction());
  }, [dispatch, isUndoDisabled]);

  const redo = useCallback(
    (source: ActionSource) => {
      if (isRedoDisabled) {
        return;
      }

      dispatch(redoAction());
      logCustomRouteUpdated({
        entry_point_location: 'revert undo',
        custom_route_mode: isSmartRouting ? 'smart draw' : 'free draw',
        smart_route_menu_activity: source === 'toolbar'
      });
    },
    [dispatch, isRedoDisabled, isSmartRouting]
  );

  const reverse = useCallback(
    async (source: ActionSource) => {
      if (!segment || isReversingRef.current) {
        return;
      }

      const hasNonFreeDrawnPaths = segment.paths.some(path => !path.freeDraw);

      if (hasNonFreeDrawnPaths) {
        isReversingRef.current = true;

        // Create the reversed segment for API re-routing: for bike and driving routes, we want the API to handle one-way streets etc.
        const reversedSegment = {
          ...segment,
          paths: [...segment.paths].reverse().map(path => ({
            ...path,
            points: [...path.points].reverse()
          }))
        };

        const result = await rerouteSegments({ segments: [reversedSegment] });

        if (result.isSuccess && result.data?.segments?.[0]) {
          dispatch(updateRouting({ segment: result.data.segments[0] }));
        } else {
          logCustomRouteUpdateFailed({ error_message: 'route cannot be calculated' });
          dispatch(reverseActiveSegment());
        }

        isReversingRef.current = false;
      } else {
        // For free-drawn paths, use the local reverse logic
        dispatch(reverseActiveSegment());
      }

      logCustomRouteUpdated({
        entry_point_location: 'reverse direction',
        custom_route_mode: isSmartRouting ? 'smart draw' : 'free draw',
        smart_route_menu_activity: source === 'toolbar'
      });
    },
    [segment, dispatch, isSmartRouting, rerouteSegments]
  );

  const doubleBack = useCallback(
    async (source: ActionSource) => {
      if (!segment || isDoubleBackingRef.current) {
        return;
      }

      const hasNonFreeDrawnPaths = segment.paths.some(path => !path.freeDraw);

      if (hasNonFreeDrawnPaths) {
        isDoubleBackingRef.current = true;
        // Create the double-back segment with return paths for API re-routing
        const returnPaths = [...segment.paths.map(path => ({ ...path, points: [...path.points] }))];
        returnPaths.reverse();
        returnPaths.forEach(path => {
          path.points.reverse();
        });

        const doubleBackSegment = {
          ...segment,
          paths: [...segment.paths, ...returnPaths]
        };

        const result = await rerouteSegments({ segments: [doubleBackSegment] });
        if (result.isSuccess && result.data?.segments?.[0]) {
          dispatch(updateRouting({ segment: result.data.segments[0] }));
        } else {
          logCustomRouteUpdateFailed({ error_message: 'route cannot be calculated' });
          dispatch(doubleBackActiveSegment());
        }

        isDoubleBackingRef.current = false;
      } else {
        // For free-drawn paths, use the local double back logic
        dispatch(doubleBackActiveSegment());
      }

      logCustomRouteUpdated({
        entry_point_location: 'double back',
        custom_route_mode: isSmartRouting ? 'smart draw' : 'free draw',
        smart_route_menu_activity: source === 'toolbar'
      });
    },
    [segment, dispatch, isSmartRouting, rerouteSegments]
  );

  const closeLoop = useCallback(
    async (source: ActionSource) => {
      if (!segment) {
        return;
      }

      let points;
      if (isSmartRouting) {
        const startPoint = segment.paths[0].points[0];
        const endPath = segment.paths[segment.paths.length - 1];
        const endPoint = endPath.points[endPath.points.length - 1];
        const { isSuccess, data } = await routing({ routes: transformPointsToServerRoutes([endPoint, startPoint]) });

        points = data?.groupedSegments?.[0]?.pathGroups?.[0]?.paths?.[0]?.points;
        if (!isSuccess || !points) {
          logCustomRouteUpdateFailed({ error_message: 'route cannot be calculated' });
          return;
        }
      }

      dispatch(closeLoopActiveSegment(points));
      logCustomRouteUpdated({
        entry_point_location: 'close loop',
        custom_route_mode: isSmartRouting ? 'smart draw' : 'free draw',
        smart_route_menu_activity: source === 'toolbar'
      });
    },
    [isSmartRouting, segment, routing, dispatch]
  );

  const selectStartPoint = useCallback(() => {
    if (!segment) {
      return;
    }

    dispatch(setSelectedHandleIndex(0));
  }, [segment, dispatch]);

  const selectEndPoint = useCallback(() => {
    if (!segment) {
      return;
    }

    dispatch(setSelectedHandleIndex(segment.paths.length));
  }, [segment, dispatch]);

  const removePoint = useCallback(async () => {
    if (!segment || selectedHandleIndex === undefined) {
      return;
    }

    let replacementPoints;
    const isStart = selectedHandleIndex === 0;
    const isEnd = selectedHandleIndex === segment.paths.length;
    if (!isStart && !isEnd) {
      const prevPath = segment.paths[selectedHandleIndex - 1];
      const path = segment.paths[selectedHandleIndex];
      const prevHandle = prevPath.points[0];
      const nextHandle = path.points[path.points.length - 1];

      // Only route if the handles before and after the handle being removed are different and at least one of the paths connected to the handle being removed is not free draw
      if (!isEqual(prevHandle, nextHandle) && (!prevPath.freeDraw || !path.freeDraw)) {
        const { isSuccess, data } = await routing({ routes: transformPointsToServerRoutes([prevHandle, nextHandle]) });
        replacementPoints = data?.groupedSegments?.[0]?.pathGroups?.[0]?.paths?.[0]?.points;

        if (!isSuccess || !replacementPoints) {
          logCustomRouteUpdateFailed({ error_message: 'route cannot be calculated' });
          return;
        }
      }
    }

    dispatch(removeActiveSegmentHandle(replacementPoints));
  }, [segment, selectedHandleIndex, routing, dispatch]);

  return {
    isUndoDisabled,
    isRedoDisabled,
    isReverseDisabled: isReverseDisabled || isReversingRef.current,
    isDoubleBackDisabled: isDoubleBackDisabled || isDoubleBackingRef.current,
    isCloseLoopDisabled,
    undo,
    redo,
    reverse,
    doubleBack,
    closeLoop,
    selectStartPoint,
    selectEndPoint,
    removePoint
  };
};

export default useSegmentActions;
